import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, DatePicker, Descriptions, Flex, Form, Input, Modal, Select, Space, Tag, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { api, unwrap, type OrderDetail } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";

type Order = OrderDetail["order"];
type Payment = "unpaid" | "paid" | "cod";

const payColor: Record<string, string> = { unpaid: "warning", paid: "success", cod: "processing" };

export function PaymentTag({ status }: { status: string }) {
  const { tDynamic } = useI18n();
  if (!status) return null;
  return <Tag color={payColor[status]}>{tDynamic(`manual.pay.${status}`, "manual.pay.unpaid")}</Tag>;
}

/**
 * A manual order's own controls (PRD OMS-5): payment, the tracking number
 * typed in, shipped and completed set by hand.
 */
export function ManualPanel({ o, tracking }: { o: Order; tracking: string }) {
  const { t, formatMoney, formatDateTime } = useI18n();
  const { can, me } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const tz = me?.workspace?.timezone;
  const manage = can("orders.manage");
  const [paying, setPaying] = useState(false);
  const [resi, setResi] = useState<{ carrier: string; trackingNo: string } | null>(null);
  const path = { params: { path: { orderID: o.id } } };
  const done = (text: "manual.saved") => {
    void message.success(t(text));
    for (const k of ["orders", "inventory", "finance", "dashboard"]) void queryClient.invalidateQueries({ queryKey: [k] });
  };
  const payment = useMutation({
    mutationFn: (body: { paymentStatus: Payment; accountId?: string | null; paidOn?: string | null }) =>
      unwrap(api.POST("/v1/orders/{orderID}/payment", { ...path, body })),
    onSuccess: () => {
      setPaying(false);
      done("manual.saved");
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const track = useMutation({
    mutationFn: (body: { carrier: string; trackingNo: string }) => unwrap(api.POST("/v1/orders/{orderID}/tracking", { ...path, body })),
    onSuccess: () => {
      setResi(null);
      done("manual.saved");
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const status = useMutation({
    mutationFn: (s: "shipped" | "completed") => unwrap(api.POST("/v1/orders/{orderID}/manual-status", { ...path, body: { status: s } })),
    onSuccess: () => done("manual.saved"),
    onError: (err) => void message.error(errorText(err)),
  });
  const closed = ["cancelled", "returned"].includes(o.status);
  const canShip = ["processing", "packed"].includes(o.status) && !!o.processedAt;

  return (
    <Card size="small" title={t("manual.panel")}>
      <Descriptions column={1} size="small">
        <Descriptions.Item label={t("manual.paymentStatus")}>
          <Space size={6} wrap>
            <PaymentTag status={o.paymentStatus} />
            {o.paidAt && o.paymentStatus === "paid" && <Typography.Text type="secondary">{formatDateTime(o.paidAt, tz)}</Typography.Text>}
          </Space>
        </Descriptions.Item>
        {o.shippingFee > 0 && <Descriptions.Item label={t("manual.shippingFee")}>{formatMoney(o.shippingFee)}</Descriptions.Item>}
        {o.discount > 0 && <Descriptions.Item label={t("manual.discount")}>{formatMoney(-o.discount)}</Descriptions.Item>}
        <Descriptions.Item label={t("manual.total")}>
          <Typography.Text strong className="z-num">
            {formatMoney(o.totalAmount)}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label={t("manual.trackingNo")}>
          {tracking ? <span className="z-num">{tracking}</span> : <Typography.Text type="warning">{t("manual.noTracking")}</Typography.Text>}
        </Descriptions.Item>
        {o.note && <Descriptions.Item label={t("po.note")}>{o.note}</Descriptions.Item>}
        {o.createdName && <Descriptions.Item label={t("po.createdBy")}>{o.createdName}</Descriptions.Item>}
      </Descriptions>
      {manage && !closed && (
        <Flex gap={8} wrap style={{ marginTop: 12 }}>
          {o.paymentStatus !== "paid" ? (
            <Button type={o.paymentStatus === "unpaid" ? "primary" : "default"} onClick={() => setPaying(true)}>
              {t("manual.markPaid")}
            </Button>
          ) : (
            <Button onClick={() => payment.mutate({ paymentStatus: "unpaid" })} loading={payment.isPending}>
              {t("manual.markUnpaid")}
            </Button>
          )}
          {o.status !== "completed" && o.status !== "shipped" && (
            <Button onClick={() => setResi({ carrier: o.carrier, trackingNo: tracking })}>{tracking ? t("manual.editTracking") : t("manual.setTracking")}</Button>
          )}
          {canShip && (
            <Button onClick={() => status.mutate("shipped")} loading={status.isPending && status.variables === "shipped"}>
              {t("manual.markShipped")}
            </Button>
          )}
          {o.status === "shipped" && (
            <Button onClick={() => status.mutate("completed")} loading={status.isPending && status.variables === "completed"}>
              {t("manual.markCompleted")}
            </Button>
          )}
        </Flex>
      )}
      <PayModal open={paying} total={o.totalAmount} loading={payment.isPending} onClose={() => setPaying(false)} onPay={(v) => payment.mutate(v)} />
      <Modal
        open={resi !== null}
        title={t("manual.setTracking")}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        confirmLoading={track.isPending}
        okButtonProps={{ disabled: !resi?.trackingNo.trim() }}
        onOk={() => resi && track.mutate({ carrier: resi.carrier.trim(), trackingNo: resi.trackingNo.trim() })}
        onCancel={() => setResi(null)}
        destroyOnHidden
      >
        <Space orientation="vertical" style={{ width: "100%" }}>
          <Input placeholder={t("manual.carrier")} maxLength={60} value={resi?.carrier} onChange={(e) => setResi((x) => x && { ...x, carrier: e.target.value })} />
          <Input
            placeholder={t("manual.trackingNo")}
            maxLength={60}
            className="z-num"
            autoFocus
            value={resi?.trackingNo}
            onChange={(e) => setResi((x) => x && { ...x, trackingNo: e.target.value })}
          />
        </Space>
      </Modal>
    </Card>
  );
}

function PayModal({ open, total, loading, onClose, onPay }: {
  open: boolean;
  total: number;
  loading: boolean;
  onClose: () => void;
  onPay: (v: { paymentStatus: Payment; accountId?: string | null; paidOn?: string | null }) => void;
}) {
  const { t, formatMoney } = useI18n();
  const { can } = useAuth();
  const [form] = Form.useForm<{ accountId?: string; paidOn: Dayjs }>();
  const accounts = useQuery({
    queryKey: ["finance", "cash", "accounts"],
    queryFn: () => unwrap(api.GET("/v1/finance/cash/accounts")),
    enabled: open && can("finance.view"),
  });
  return (
    <Modal open={open} title={t("manual.markPaid")} okText={t("manual.markPaid")} cancelText={t("common.cancel")} confirmLoading={loading} onOk={() => form.submit()} onCancel={onClose} destroyOnHidden>
      <Typography.Paragraph>{t("manual.payBody", { amount: formatMoney(total) })}</Typography.Paragraph>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ paidOn: dayjs() }}
        onFinish={(v) => onPay({ paymentStatus: "paid", accountId: v.accountId ?? null, paidOn: v.paidOn.toISOString() })}
      >
        {can("finance.view") && (
          <Form.Item name="accountId" label={t("manual.paidInto")} extra={t("manual.paidIntoHelp")}>
            <Select allowClear options={(accounts.data?.items ?? []).filter((a) => a.active).map((a) => ({ value: a.id, label: a.name }))} />
          </Form.Item>
        )}
        <Form.Item name="paidOn" label={t("manual.paidOn")}>
          <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} allowClear={false} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
